import { Routes } from "@lifeat/shared/system/RouteConstants";
import * as amplitude from "@amplitude/analytics-browser";
import { AmplitudeEvent } from "./AmplitudeEvents";

const enableAmplitude = process.env.ENABLE_AMPLITUDE === "true";
class AmplitudeService {
    /* Provides an interface to Amplitude for logging events */
    private static instance: AmplitudeService; // Singleton instance
    amplitudeInstance: typeof amplitude;

    private constructor(apiKey: string) {
        this.amplitudeInstance = amplitude;
        if (this.amplitudeInstance) {
            this.amplitudeInstance.init(apiKey, undefined, {
                appVersion: process.env.LIFEAT_VERSION,
                defaultTracking: {
                    sessions: false,
                    pageViews: {
                        trackOn: () => {
                            return (
                                window.location.pathname === "/" ||
                                window.location.pathname.includes(Routes.ReferralPage)
                            );
                        },
                    },
                },
            });
        }
    }

    static getInstance() {
        if (!this.instance) {
            if (!process.env.AMPLITUDE_API_KEY) {
                throw new Error("Amplitude API Key not provided.");
            }
            this.instance = new AmplitudeService(process.env.AMPLITUDE_API_KEY);
        }
        return this.instance;
    }

    setUserId(userId: string) {
        if (!enableAmplitude) {
            return;
        }
        if (process.env.CLIENT_LOG === "true") {
            console.log(`AMP Set User ID`);
        }
        this.amplitudeInstance?.setUserId(userId);
    }

    // https://www.docs.developers.amplitude.com/data/sdks/typescript-browser/#reset-when-user-logs-out
    unsetUserId() {
        if (!enableAmplitude) {
            return;
        }
        if (process.env.CLIENT_LOG === "true") {
            console.log(`AMP UN-set User ID`);
        }
        this.amplitudeInstance?.reset(); //set uid to undefined and set deviceId to a new UUID
    }

    setUserPropOnce(propKey: string, propValue: string | number | boolean) {
        if (!enableAmplitude) {
            return;
        }
        if (process.env.CLIENT_LOG === "true") {
            console.log(`AMP Set User Prop ONCE [key: ${propKey}, value: ${propValue}]`);
        }
        const identify = new amplitude.Identify().setOnce(propKey, propValue);
        this.amplitudeInstance?.identify(identify);
    }

    setUserProp(propKey: string, propValue: string | number | boolean | Array<string | number>) {
        if (!enableAmplitude) {
            return;
        }
        if (process.env.CLIENT_LOG === "true") {
            console.log(`AMP Set User Prop [key: ${propKey}, value: ${propValue}]`);
        }
        const identify = new amplitude.Identify().set(propKey, propValue);
        this.amplitudeInstance?.identify(identify);
    }

    logEvent(event: AmplitudeEvent) {
        if (!enableAmplitude) {
            return;
        }
        if (process.env.CLIENT_LOG === "true") {
            console.log(`AMP Event: ${JSON.stringify(event)}`);
        }

        this.amplitudeInstance?.track(event.name, {
            ...event.props,
            isDesktopApp: !!window.todesktop,
            lifeatVersion: process.env.LIFEAT_VERSION,
        });
    }
}

export { AmplitudeService };
